/* Payment verification modal (prototype verifyModal): receipt preview, AI match against the
 * invoice, checklist, verify & activate / reject with reason. */
import { useId, useState } from "react";
import { useData } from "../../../data";
import { ApiError } from "../../../data/provider";
import { useRejectPayment, useVerifyPayment } from "../../../data/hooks";
import type { Payment } from "../../../data/types";
import { useI18n } from "../../../i18n";
import { gDate, moneyText } from "../../../lib/format";
import { Alert, Button, Chip, Field, Grid, Icon, Input, Kv, Modal, Money, Select, errMsg, useToast } from "../../../ui";
import { sampleReceiptSvg } from "../../billing/receipt";

export const REJECT_REASONS: [string, string, string][] = [
  ["Amount doesn't match", "Amount doesn't match", "المبلغ غير مطابق"],
  ["Transfer not received in bank", "Transfer not received in bank", "لم يصل التحويل إلى الحساب"],
  ["Reference code missing", "Reference code missing", "رمز المرجع مفقود"],
  ["Receipt unreadable", "Receipt unreadable", "الإيصال غير واضح"],
  ["other", "Other reason…", "سبب آخر…"],
];

/** Checks shown to the verifier; all true = "Looks good to verify". */
export function paymentChecks(p: Payment) {
  const ai = p.ai ?? null;
  return {
    amount: Math.abs(p.paid - p.total) < 0.5,
    ref: ai ? ai.ref === p.ref : !!p.ref,
    date: ai ? ai.date === p.date : true,
    aiAmount: ai ? Math.abs(ai.amount - p.total) < 0.5 : null,
  };
}
export const looksGood = (p: Payment) => { const c = paymentChecks(p); return c.amount && c.ref && c.date && c.aiAmount !== false; };

export function VerifyModal({ p, onClose }: { p: Payment; onClose: () => void }) {
  const { t, L, lang } = useI18n();
  const data = useData();
  const toast = useToast();
  const verify = useVerifyPayment();
  const reject = useRejectPayment();
  const [reason, setReason] = useState(REJECT_REASONS[0][0]);
  const [other, setOther] = useState("");
  const [err, setErr] = useState<string | undefined>();
  const selId = useId(), otherId = useId();
  const pending = p.status === "Pending";
  const ai = p.ai ?? null;
  const c = paymentChecks(p);
  const ok = looksGood(p);
  const src = p.receiptKey ? data.fileUrl(p.receiptKey) : sampleReceiptSvg({ paid: ai?.amount ?? p.paid, ref: ai ? ai.ref : p.ref, date: gDate(ai?.date ?? p.date, "en"), bank: p.bank, id: p.id });
  const rows: [boolean, string, string][] = [
    [c.amount, L("Amount matches invoice total", "المبلغ يطابق الإجمالي"), moneyText(p.paid, lang) + " / " + moneyText(p.total, lang)],
    [c.ref, L("Reference code found in transfer note", "رمز المرجع موجود في وصف التحويل"), ai ? (ai.ref || L("not found on receipt", "غير موجود في الإيصال")) : p.ref],
    [c.date, L("Receipt date matches the transfer date", "تاريخ الإيصال يطابق تاريخ التحويل"), gDate(ai?.date ?? p.date, lang)],
    [true, L("Receipt not used before", "الإيصال غير مستخدم سابقًا"), L("No duplicate found", "لا يوجد تكرار")],
  ];
  if (c.aiAmount != null) rows.splice(1, 0, [c.aiAmount, L("AI-read amount matches the invoice", "المبلغ المقروء آليًا يطابق الفاتورة"), moneyText(ai!.amount, lang) + " / " + moneyText(p.total, lang)]);

  const done = (e: unknown) => {
    if (e instanceof ApiError && e.status === 409) { toast(L("This payment was already processed.", "تمت معالجة هذه الدفعة بالفعل."), "bad"); onClose(); return; }
    if (e instanceof ApiError && e.fields?.reason) { setErr(e.fields.reason); return; }
    toast(errMsg(e), "bad");
  };
  const doVerify = async () => {
    try { await verify.mutateAsync(p.id); toast(L("Payment verified · " + p.tenant + " is now active", "تم التحقق · اشتراك " + p.tenant + " مفعل")); onClose(); } catch (e) { done(e); }
  };
  const doReject = async () => {
    const r = reason === "other" ? other.trim() : reason;
    if (!r) { setErr(L("Write the reason the customer will see", "اكتب السبب الذي سيظهر للعميل")); return; }
    try { await reject.mutateAsync({ id: p.id, reason: r }); toast(L("Receipt rejected · customer notified", "تم الرفض وإبلاغ العميل"), "bad"); onClose(); } catch (e) { done(e); }
  };

  return (
    <Modal title={p.tenant + " · " + p.id} className="wide" onClose={onClose} testId="verify-modal"
      foot={pending
        ? <><Button variant="danger" onClick={doReject} loading={reject.isPending} disabled={verify.isPending} data-testid="btn-reject">{t("Reject")}</Button><Button variant="pri" icon="check" onClick={doVerify} loading={verify.isPending} disabled={reject.isPending} data-testid="btn-verify">{L("Verify & activate", "تحقق وفعّل")}</Button></>
        : <Button onClick={onClose}>{t("Close")}</Button>}>
      <Grid cols="2">
        <div className="stack">
          <img src={src} alt={L("Bank transfer receipt", "إيصال التحويل البنكي")} data-testid="verify-receipt" style={{ width: "100%", maxHeight: 440, objectFit: "contain", border: "1px solid var(--border)", borderRadius: 12, background: "var(--sunken)" }} />
          <span className="xs muted">{L("Uploaded by the customer · ", "رفعه العميل · ")}{gDate(p.date, lang)}</span>
        </div>
        <div className="stack">
          {pending && (
            <Alert tone={ok ? "ok" : "warn"} tint testId="verify-ai"
              title={<><span className="aitag"><Icon name="sparkle" className="s" />AI</span> {ok ? L("Looks good to verify", "جاهز للتحقق") : L("Needs a closer look", "يحتاج مراجعة")}</>}
              desc={ai ? L(`Read from the receipt with ${ai.conf}% confidence. Still confirm the credit on the bank statement.`, `قُرئ من الإيصال بدقة ${ai.conf}%. تأكد من القيد في كشف الحساب.`) : L("No AI reading for this receipt — compare it with the bank statement manually.", "لا توجد قراءة آلية لهذا الإيصال — قارنه بكشف الحساب يدويًا.")} />
          )}
          <div className="stack" style={{ gap: 6 }} data-testid="verify-checks">
            {rows.map(([good, label, detail]) => (
              <div key={label} className="list-row" style={{ minHeight: 44 }} data-ok={good ? "1" : "0"}>
                <span className={"kpi-ico " + (good ? "" : "warn")} style={{ width: 28, height: 28 }}><Icon name={good ? "check" : "alert"} className="s" /></span>
                <div className="grow"><div className="sm">{label}</div><div className="xs muted">{detail}</div></div>
              </div>
            ))}
          </div>
          <Kv items={[[L("Plan", "الباقة"), p.plan + " · " + p.seats + " · " + (p.cycle === "y" ? t("Annually") : t("Monthly"))], [L("Invoice total", "إجمالي الفاتورة"), <Money key="m" value={p.total} />], [L("Sender", "المحوّل"), p.sender + " · " + p.bank], ["CR", <span key="c" className="num">{p.cr}</span>], [t("Status"), <Chip key="s" status={p.status === "Verified" ? "Approved" : p.status} />]]} />
          {pending ? (
            <>
              <Field label={L("Rejection reason (if rejecting)", "سبب الرفض (عند الرفض)")} htmlFor={selId} err={reason !== "other" ? err : undefined}>
                <Select id={selId} value={reason} onChange={(e) => { setReason(e.target.value); setErr(undefined); }} options={REJECT_REASONS.map((r) => [r[0], L(r[1], r[2])] as [string, string])} data-testid="reject-reason" />
              </Field>
              {reason === "other" && (
                <Field label={L("Reason shown to the customer", "السبب الظاهر للعميل")} htmlFor={otherId} err={err}>
                  <Input id={otherId} value={other} bad={!!err} onChange={(e) => { setOther(e.target.value); setErr(undefined); }} data-testid="reject-other" />
                </Field>
              )}
            </>
          ) : p.reason ? <Alert tone="bad" title={t("Rejected")} desc={p.reason} /> : p.by ? <p className="xs muted" style={{ margin: 0 }}>{L("Verified by ", "تحقق بواسطة ")}{p.by}{p.at ? " · " + gDate(p.at, lang) : ""}</p> : null}
        </div>
      </Grid>
    </Modal>
  );
}
